/**
 * 加载组件
 * 各种加载状态的指示器
 */

import React from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  type?: 'spinner' | 'dots' | 'pulse';
  text?: string;
  className?: string;
}

const Loading: React.FC<LoadingProps> = ({
  size = 'md',
  type = 'spinner',
  text,
  className
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  if (type === 'spinner') {
    return (
      <div className={clsx('flex items-center justify-center', className)}>
        <div className="flex flex-col items-center space-y-2">
          <svg
            className={clsx('animate-spin text-primary-600', sizeClasses[size])}
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {text && (
            <p className={clsx('text-gray-600', textSizeClasses[size])}>
              {text}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (type === 'dots') {
    const dotVariants = {
      initial: { y: 0 },
      animate: {
        y: [-4, 0, -4],
        transition: {
          duration: 0.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      }
    };

    const dotSize = {
      sm: 'w-1 h-1',
      md: 'w-2 h-2',
      lg: 'w-3 h-3'
    };

    return (
      <div className={clsx('flex items-center justify-center', className)}>
        <div className="flex flex-col items-center space-y-4">
          <div className="flex space-x-1">
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                className={clsx('bg-primary-600 rounded-full', dotSize[size])}
                variants={dotVariants}
                initial="initial"
                animate="animate"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              />
            ))}
          </div>
          {text && (
            <p className={clsx('text-gray-600', textSizeClasses[size])}>
              {text}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (type === 'pulse') {
    const pulseSize = {
      sm: 'w-8 h-8',
      md: 'w-12 h-12',
      lg: 'w-16 h-16'
    };

    return (
      <div className={clsx('flex items-center justify-center', className)}>
        <div className="flex flex-col items-center space-y-4">
          <motion.div
            className={clsx('bg-primary-600 rounded-full', pulseSize[size])}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [1, 0.7, 1]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
          {text && (
            <p className={clsx('text-gray-600', textSizeClasses[size])}>
              {text}
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default Loading;